import { ReactNode } from "react";
import {
  BiCheck,
  BiCheckCircle,
  BiCloudRain,
  BiTransferAlt,
  BiWifiOff,
  BiWind,
} from "react-icons/bi";

const Panel = ({ children }: { children: ReactNode }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-black/30 backdrop-blur">
    {children}
  </div>
);

const PackingMock = () => (
  <Panel>
    <div className="flex items-center justify-between text-xs text-slate-400">
      <span>Packing list · Saturday</span>
      <span className="inline-flex items-center gap-1 text-sky-300">
        <BiWind size={14} /> Gusts 24 mph
      </span>
    </div>
    <ul className="mt-4 space-y-2.5 text-sm">
      {[
        { label: "40 lb weights per canopy leg + tie-downs", done: true },
        { label: "Tarp and lidded bins (40% rain)", done: true, icon: true },
        { label: "Extra water and shade (high of 88°F)", done: false },
      ].map((row) => (
        <li key={row.label} className="flex items-center gap-3 text-slate-200">
          <span
            className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${
              row.done
                ? "border-blue-500 bg-blue-500 text-white"
                : "border-white/20"
            }`}
          >
            {row.done && <BiCheck size={14} />}
          </span>
          <span className={row.done ? "text-slate-400 line-through" : ""}>
            {row.label}
          </span>
          {row.icon && <BiCloudRain className="ml-auto text-sky-300" />}
        </li>
      ))}
    </ul>
  </Panel>
);

const CheckoutMock = () => (
  <Panel>
    <div className="space-y-2 text-sm">
      <div className="flex justify-between text-slate-300">
        <span>Hand-poured candle ×2</span>
        <span>$64.00</span>
      </div>
      <div className="flex justify-between text-slate-300">
        <span>Discount</span>
        <span className="text-emerald-300">−$6.00</span>
      </div>
      <div className="flex justify-between text-slate-300">
        <span>Sales tax</span>
        <span>$3.48</span>
      </div>
      <div className="flex justify-between border-t border-white/10 pt-2 text-base font-semibold text-white">
        <span>Total</span>
        <span>$61.48</span>
      </div>
    </div>
    <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs font-medium">
      {["Card", "Cash", "QR", "Split"].map((method, i) => (
        <span
          key={method}
          className={`rounded-lg py-2 ${
            i === 0
              ? "bg-blue-600 text-white"
              : "border border-white/10 text-slate-300"
          }`}
        >
          {method}
        </span>
      ))}
    </div>
  </Panel>
);

const OfflineMock = () => (
  <Panel>
    <div className="flex items-center gap-3 rounded-xl bg-amber-400/10 px-3 py-2.5 text-sm text-amber-200">
      <BiWifiOff size={18} />
      No signal · 3 sales saved on this phone
    </div>
    <div className="my-3 ml-5 h-5 border-l border-dashed border-white/20" />
    <div className="flex items-center gap-3 rounded-xl bg-emerald-400/10 px-3 py-2.5 text-sm text-emerald-200">
      <BiCheckCircle size={18} />
      Back online · all sales synced
    </div>
  </Panel>
);

const LowStockMock = () => (
  <Panel>
    <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">
      Low stock
    </p>
    <p className="mt-1.5 font-semibold text-white">
      Trucker hats: 6 left at State Fair
    </p>
    <div className="mt-4 flex items-center gap-3 rounded-xl border border-white/10 p-3 text-sm">
      <span className="rounded-lg bg-white/5 px-2.5 py-1 text-slate-300">
        Main
      </span>
      <BiTransferAlt className="text-blue-300" size={18} />
      <span className="rounded-lg bg-white/5 px-2.5 py-1 text-slate-300">
        State Fair
      </span>
      <span className="ml-auto font-semibold text-white">+12</span>
    </div>
  </Panel>
);

const CloseOutMock = () => (
  <Panel>
    <p className="text-xs text-slate-400">State Fair · closed</p>
    <p className="mt-1 text-3xl font-bold tracking-tight text-white">
      $1,829.96
      <span className="ml-2 align-middle text-sm font-semibold text-emerald-300">
        net profit
      </span>
    </p>
    <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
      {[
        ["Revenue", "$2,052.28"],
        ["Costs", "$158.00"],
        ["Staff pay", "$64.32"],
      ].map(([label, value]) => (
        <div key={label} className="rounded-lg bg-white/5 p-2.5">
          <p className="text-slate-400">{label}</p>
          <p className="mt-0.5 font-semibold text-white">{value}</p>
        </div>
      ))}
    </div>
  </Panel>
);

const moments = [
  {
    time: "6:30 AM",
    title: "Pack for the forecast",
    description:
      "A packing list built from the weather at your venue: canopy weights for wind, rain gear when it might pour, and shade when it’s hot.",
    mock: <PackingMock />,
  },
  {
    time: "9:00 AM",
    title: "Doors open, checkout ready",
    description:
      "Ring up items with discounts and tax worked out for you. Take card, cash, QR, or split one sale across several payments.",
    mock: <CheckoutMock />,
  },
  {
    time: "12:15 PM",
    title: "No signal? Keep taking cash",
    description:
      "Fairgrounds and basements drop out. Cash and QR code payments are saved on your phone and sync on their own when you’re back online. Square card payments need a connection.",
    mock: <OfflineMock />,
  },
  {
    time: "2:40 PM",
    title: "Restock before the rush",
    description:
      "Low-stock alerts flag a best seller before it runs out, and inventory pools let you move stock between shows in a tap.",
    mock: <LowStockMock />,
  },
  {
    time: "6:00 PM",
    title: "Close out with real numbers",
    description:
      "Revenue, costs, refunds, staff pay, and profit for the show, ready before the tent comes down.",
    mock: <CloseOutMock />,
  },
];

const BoothDay = () => (
  <section className="relative isolate overflow-hidden bg-[#070b17] px-5 py-24 text-white md:px-8 md:py-32">
    <div
      aria-hidden
      className="absolute left-1/2 top-1/3 -z-10 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-indigo-600/10 blur-[140px]"
    />
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-400">
        A day at the booth
      </p>
      <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">
        From load-in to load-out, handled.
      </h2>
      <p className="mt-4 text-lg text-slate-400">
        Here’s how a market day goes with VendorOrbis in your pocket.
      </p>
    </div>

    <ol className="relative mx-auto mt-16 max-w-5xl md:mt-20">
      {/* The timeline rail: left edge on phones, centered on desktop */}
      <div
        aria-hidden
        className="absolute bottom-4 left-[11px] top-4 w-px bg-gradient-to-b from-blue-500/0 via-blue-500/50 to-violet-500/0 md:left-1/2"
      />
      {moments.map((moment, index) => {
        const textFirst = index % 2 === 0;
        return (
          <li
            key={moment.time}
            className="relative grid gap-6 pb-14 pl-10 last:pb-0 md:grid-cols-2 md:gap-16 md:pl-0"
          >
            <span
              aria-hidden
              className="absolute left-0 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-blue-400/50 bg-[#070b17] md:left-1/2 md:-translate-x-1/2"
            >
              <span className="h-2 w-2 rounded-full bg-blue-400 shadow-[0_0_12px_2px_rgba(96,165,250,0.8)]" />
            </span>
            <div
              className={`${
                textFirst ? "md:text-right" : "md:order-2"
              } md:pt-0.5`}
            >
              <p className="font-mono text-sm font-semibold text-blue-300">
                {moment.time}
              </p>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight">
                {moment.title}
              </h3>
              <p
                className={`mt-3 leading-7 text-slate-400 ${
                  textFirst ? "md:ml-auto" : ""
                } max-w-md`}
              >
                {moment.description}
              </p>
            </div>
            <div className={textFirst ? "" : "md:order-1"}>
              <div className={`max-w-md ${textFirst ? "" : "md:ml-auto"}`}>
                {moment.mock}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  </section>
);

export default BoothDay;
